import React from "react";
import { motion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Clock, Award } from "lucide-react";
import { BUSINESS, HERO_BG } from "../../lib/constants";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-navy text-white"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Junk removal crew loading a truck"
          className="h-full w-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-navy/90 via-navy/70 to-navy/95" />
      </div>

      {/* Diagonal gold stripe */}
      <div className="absolute -right-24 top-0 h-full w-56 bg-gold/15 -skew-x-12 hidden md:block" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32 min-h-[88vh] flex flex-col justify-center">
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.5 }}
          className="mb-6 flex flex-wrap items-center gap-3"
        >
          <span className="label-pill !bg-gold !border-gold !text-navy">
            <span className="h-2 w-2 rounded-full bg-navy animate-pulse" />
            Now booking weekend jobs
          </span>
          <span className="label-pill !bg-transparent !border-white/60 !text-white">
            RN-Owned · Minority-Owned · Fully Insured
          </span>
        </motion.div>

        <motion.h1
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.08 }}
          data-testid="hero-h1"
          className="font-display uppercase text-white text-[2.75rem] leading-[0.95] sm:text-6xl lg:text-[5.5rem] max-w-5xl"
        >
          Syracuse's Trusted{" "}
          <br className="hidden sm:block" />
          <span className="text-gold">Junk Removal</span> &amp; Light{" "}
          <br className="hidden sm:block" />
          Demo Pros.
        </motion.h1>

        <motion.p
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.18 }}
          className="mt-8 text-base sm:text-lg max-w-3xl text-white/85 leading-relaxed"
        >
          Need junk gone without dealing with a big corporate junk company,
          but not just some guys with a truck?{" "}
          <strong className="text-gold font-semibold">
            B&amp;C Junk Removal Pros
          </strong>{" "}
          provides owner-operated junk removal across Syracuse and Central
          New York. We handle furniture, appliances, garage cleanouts, basement
          cleanouts, yard debris, and light demo debris. Send a few photos,
          get a fast quote, and we'll take care of the loading, hauling,
          disposal, and cleanup.
        </motion.p>

        <motion.div
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.28 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            href={BUSINESS.phoneHref}
            data-testid="hero-call-btn"
            className="brutal-btn flash inline-flex items-center justify-center gap-3 bg-gold text-navy px-7 py-5 font-display text-base sm:text-lg uppercase tracking-wider border-2 border-gold"
          >
            <Phone className="h-5 w-5" strokeWidth={3} />
            Call or Text for a Free Quote
          </a>
          <a
            href="#quote"
            data-testid="hero-quote-btn"
            className="inline-flex items-center justify-center gap-3 bg-transparent text-white px-7 py-5 font-display text-base sm:text-lg uppercase tracking-wider border-2 border-white hover:bg-white hover:text-navy transition-colors"
          >
            Or fill out the form
            <ArrowRight className="h-5 w-5" strokeWidth={3} />
          </a>
        </motion.div>

        {/* Trust strip */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/15 border-2 border-white/15 max-w-3xl"
        >
          {[
            { icon: ShieldCheck, label: "Fully Insured" },
            { icon: Clock, label: "Weekends Only" },
            { icon: Award, label: "5★ Local Crew" },
            { icon: Phone, label: "Sat & Sun 8a–8p" },
          ].map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="bg-navy/70 backdrop-blur px-4 py-4 flex items-center gap-3"
            >
              <Icon className="h-5 w-5 text-gold shrink-0" strokeWidth={2.5} />
              <span className="font-body text-xs sm:text-sm uppercase tracking-wider font-bold">
                {label}
              </span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Bottom marquee */}
      <div className="relative border-t-2 border-b-2 border-gold bg-navy-dark overflow-hidden">
        <div className="marquee py-3 text-gold font-display uppercase text-sm tracking-[0.3em]">
          {["marquee-a", "marquee-b"].map((k) => (
            <div key={k} className="flex gap-12 shrink-0">
              <span>★ Syracuse Strong. Junk Gone.</span>
              <span>★ Residential + Commercial</span>
              <span>★ Light Demolition</span>
              <span>★ 50-Mile Radius</span>
              <span>★ Free Quotes</span>
              <span>★ Minority Owned</span>
              <span>★ Weekend Service</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

